import { createReducer, on } from '@ngrx/store';
import { concat, set, toLower, uniqBy } from 'lodash';
import { QuestionnaireTenxActions } from './tenx.actions';
import { QUESTIONNAIRE_TENX_DATA } from './tenx.data';
import {
  QuestionnaireTenxState,
  intialQuestionnaireTenxState,
  questionnaireTenxState
} from './tenx.state';

export const questionnaireTenxReducer = createReducer(
  questionnaireTenxState,
  on(
    QuestionnaireTenxActions.reset,
    (): QuestionnaireTenxState => intialQuestionnaireTenxState()
  ),
  on(QuestionnaireTenxActions.setForm, (state, { key, value }) => ({
    ...state,
    form: {
      ...state.form,
      ...set(state.form, key, value)
    }
  })),
  on(
    QuestionnaireTenxActions.setUserProfile,
    (state, { value: profile }): QuestionnaireTenxState => {
      let form = {
        ...state.form
      };

      if (profile?.information?.height) {
        form = {
          ...form,
          information: {
            ...form.information,
            height: profile.information.height
          }
        };
      }
      if (profile?.information?.weight) {
        form = {
          ...form,
          information: {
            ...form.information,
            weight: profile.information.weight
          }
        };
      }
      if (profile?.lifestyle?.sleepHabits.sleepEachNightOnAverage) {
        const found = (
          QUESTIONNAIRE_TENX_DATA['sleepHabits.sleepEachNightOnAverage']
            ?.options ?? []
        ).find(
          (o) =>
            toLower(o.name) ===
            toLower(
              profile.lifestyle?.sleepHabits.sleepEachNightOnAverage ?? ''
            )
        );
        form = {
          ...form,
          sleepHabits: {
            ...form.sleepHabits,
            sleepEachNightOnAverage: found ?? {
              id: '001',
              name: profile.lifestyle.sleepHabits.sleepEachNightOnAverage,
              value: profile.lifestyle.sleepHabits.sleepEachNightOnAverage
            }
          }
        };
      }
      if (profile?.lifestyle?.smokingHabits) {
        if (profile.lifestyle.smokingHabits.smokingStatus) {
          const found = (
            QUESTIONNAIRE_TENX_DATA['smokingHabits.smokingStatus']?.options ??
            []
          ).find(
            (o) =>
              toLower(o.name) ===
              toLower(profile.lifestyle?.smokingHabits.smokingStatus ?? '')
          );
          form = {
            ...form,
            smokingHabits: {
              ...form.smokingHabits,
              smokingStatus: found ?? {
                id: '001',
                name: profile.lifestyle.smokingHabits.smokingStatus,
                value: profile.lifestyle.smokingHabits.smokingStatus
              }
            }
          };
        }
        if (profile.lifestyle.smokingHabits.startSmoking) {
          const found = (
            QUESTIONNAIRE_TENX_DATA['smokingHabits.startSmoking']?.options ?? []
          ).find(
            (o) =>
              toLower(o.name) ===
              toLower(profile.lifestyle?.smokingHabits.startSmoking ?? '')
          );
          form = {
            ...form,
            smokingHabits: {
              ...form.smokingHabits,
              startSmoking: found ?? {
                id: '001',
                name: profile.lifestyle.smokingHabits.startSmoking,
                value: profile.lifestyle.smokingHabits.startSmoking
              }
            }
          };
        }
        if (profile.lifestyle.smokingHabits.dailyConsumption) {
          const found = (
            QUESTIONNAIRE_TENX_DATA['smokingHabits.dailyConsumption']
              ?.options ?? []
          ).find(
            (o) =>
              toLower(o.name) ===
              toLower(profile.lifestyle?.smokingHabits.dailyConsumption ?? '')
          );
          form = {
            ...form,
            smokingHabits: {
              ...form.smokingHabits,
              dailyConsumption: found ?? {
                id: '001',
                name: profile.lifestyle.smokingHabits.dailyConsumption,
                value: profile.lifestyle.smokingHabits.dailyConsumption
              }
            }
          };
        }
        if ((profile.lifestyle.smokingHabits.cigarettesType ?? []).length > 0) {
          const typeOfCigarettes =
            profile.lifestyle.smokingHabits.cigarettesType ?? [];
          const selected = [];
          const selectedOthers = [];

          for (const cigarette of typeOfCigarettes) {
            const found = (
              QUESTIONNAIRE_TENX_DATA['smokingHabits.cigarettesType']
                ?.options ?? []
            ).find((o) => toLower(o.name) === toLower(cigarette));

            if (found) {
              selected.push(found);
            } else {
              selectedOthers.push(cigarette);
            }
          }
          form = {
            ...form,
            smokingHabits: {
              ...form.smokingHabits,
              cigarettesType: concat(selected, [
                {
                  name: selectedOthers.map((v) => v).join(', '),
                  value: 'OTHER'
                }
              ])
            }
          };
        }
      }
      if (profile?.lifestyle?.alcoholHabits) {
        if (profile.lifestyle.alcoholHabits.alcoholStatus) {
          const found = (
            QUESTIONNAIRE_TENX_DATA['alcoholHabits.alcoholStatus']?.options ??
            []
          ).find(
            (o) =>
              toLower(o.name) ===
              toLower(profile.lifestyle?.alcoholHabits.alcoholStatus ?? '')
          );
          form = {
            ...form,
            alcoholHabits: {
              ...form.alcoholHabits,
              alcoholStatus: found ?? {
                id: '001',
                name: profile.lifestyle.alcoholHabits.alcoholStatus,
                value: profile.lifestyle.alcoholHabits.alcoholStatus
              }
            }
          };
        }
        if (profile.lifestyle.alcoholHabits.startConsumeAlcohol) {
          const found = (
            QUESTIONNAIRE_TENX_DATA['alcoholHabits.startConsumeAlcohol']
              ?.options ?? []
          ).find(
            (o) =>
              toLower(o.name) ===
              toLower(
                profile.lifestyle?.alcoholHabits.startConsumeAlcohol ?? ''
              )
          );
          form = {
            ...form,
            alcoholHabits: {
              ...form.alcoholHabits,
              startConsumeAlcohol: found ?? {
                id: '001',
                name: profile.lifestyle.alcoholHabits.startConsumeAlcohol,
                value: profile.lifestyle.alcoholHabits.startConsumeAlcohol
              }
            }
          };
        }
        if (profile.lifestyle.alcoholHabits.amountOfAlcohol) {
          const found = (
            QUESTIONNAIRE_TENX_DATA['alcoholHabits.amountOfAlcohol']?.options ??
            []
          ).find(
            (o) =>
              toLower(o.name) ===
              toLower(profile.lifestyle?.alcoholHabits.amountOfAlcohol ?? '')
          );
          form = {
            ...form,
            alcoholHabits: {
              ...form.alcoholHabits,
              amountOfAlcohol: found ?? {
                id: '001',
                name: profile.lifestyle.alcoholHabits.amountOfAlcohol,
                value: profile.lifestyle.alcoholHabits.amountOfAlcohol
              }
            }
          };
        }
        if ((profile.lifestyle.alcoholHabits.typeOfAlcohol ?? []).length > 0) {
          const typeOfAlcohol =
            profile.lifestyle.alcoholHabits.typeOfAlcohol ?? [];
          const selected = [];
          const selectedOthers = [];

          for (const alcohol of typeOfAlcohol) {
            const found = (
              QUESTIONNAIRE_TENX_DATA['alcoholHabits.typeOfAlcohol']?.options ??
              []
            ).find((o) => toLower(o.name) === toLower(alcohol));

            if (found) {
              selected.push(found);
            } else {
              selectedOthers.push(alcohol);
            }
          }
          form = {
            ...form,
            alcoholHabits: {
              ...form.alcoholHabits,
              typeOfAlcohol: concat(selected, [
                {
                  name: selectedOthers.map((v) => v).join(', '),
                  value: 'OTHER'
                }
              ])
            }
          };
        }
      }
      if ((profile?.allergy ?? []).length > 0) {
        let valueAllergies = {};
        const foodAllergies = (profile?.allergy ?? [])
          .map((o) => ({ ...o, type: o.type ?? 'FOOD' }))
          .filter((o) =>
            Array.isArray(o.type)
              ? (o.type as Array<string>)
                  .map((v) => toLower(v))
                  .includes('food')
              : typeof o.type === 'string' && toLower(o.type) === 'food'
          )
          .map((o) => ({
            name: o.name,
            value: o.name,
            antidote: o.antidote
          }));
        const conditionalAllergies = (profile?.allergy ?? [])
          .filter((o) =>
            Array.isArray(o.type)
              ? (o.type as Array<string>)
                  .map((v) => toLower(v))
                  .includes('conditional')
              : typeof o.type === 'string' && toLower(o.type) === 'conditional'
          )
          .map((o) => ({
            name: o.name,
            value: o.name,
            antidote: o.antidote
          }));
        if (foodAllergies.length > 0) {
          const selected = [];
          const selectedOthers = [];

          for (const allergy of foodAllergies) {
            const found = (
              QUESTIONNAIRE_TENX_DATA.allergies?.options ?? []
            ).find((o) => toLower(o.name) === toLower(allergy.name));

            if (found) {
              selected.push(found);
            } else {
              selectedOthers.push(allergy);
            }
          }
          valueAllergies = {
            ...valueAllergies,
            allergies: concat(
              selected,
              selectedOthers.length > 0
                ? [
                    {
                      name: selectedOthers.map((o) => o.name).join(', '),
                      value: 'OTHER'
                    }
                  ]
                : []
            )
          };
        }
        if (conditionalAllergies.length > 0) {
          const selected = [];
          const selectedOthers = [];

          for (const allergy of conditionalAllergies) {
            const found = (
              QUESTIONNAIRE_TENX_DATA.conditionalAllergies?.options ?? []
            ).find((o) => toLower(o.name) === toLower(allergy.name));

            if (found) {
              selected.push(found);
            } else {
              selectedOthers.push(allergy);
            }
          }
          valueAllergies = {
            ...valueAllergies,
            conditionalAllergies: concat(
              selected,
              selectedOthers.length > 0
                ? [
                    {
                      name: selectedOthers.map((o) => o.name).join(', '),
                      value: 'OTHER'
                    }
                  ]
                : []
            )
          };
        }
        form = {
          ...form,
          ...valueAllergies
        };
      }
      if (profile?.lifestyle?.exerciseLevelPerWeek) {
        form = {
          ...form,
          exerciseLevelPerWeek: {
            name: profile.lifestyle.exerciseLevelPerWeek,
            value: profile.lifestyle.exerciseLevelPerWeek,
            description: profile.lifestyle.exerciseLevelPerWeekCompleted
          }
        };
      }
      if (profile?.medicalHistory && profile.medicalHistory.length > 0) {
        let valueMedicalHistory = {};
        const medicalHistory = uniqBy(
          profile.medicalHistory
            .filter(
              (o) =>
                toLower(o.medicalType) !== 'illness' &&
                toLower(o.medicalType) !== 'surgical'
            )
            .map((o) => ({
              name: o.medicalNameEn,
              value: o.medicalNameEn,
              medicalType: o.medicalType || 'DISEASE',
              medicalIsOther: o.medicalIsOther || false
            })),
          'name'
        );
        const fatherMedicalHistory = uniqBy(
          profile.medicalHistory
            .filter((o) => toLower(o.medicalType) === 'illness')
            .map((o) => ({
              name: o.medicalNameEn,
              value: o.medicalNameEn,
              medicalType: o.medicalType,
              medicalIsOther: o.medicalIsOther || false
            })),
          'name'
        );
        const motherMedicalHistory = uniqBy(
          profile.medicalHistory
            .filter((o) => toLower(o.medicalType) === 'illness')
            .map((o) => ({
              name: o.medicalNameEn,
              value: o.medicalNameEn,
              medicalType: o.medicalType,
              medicalIsOther: o.medicalIsOther || false
            })),
          'name'
        );
        if (medicalHistory.length > 0) {
          const selected = [];
          const selectedOthers = [];

          for (const medical of medicalHistory) {
            const found = (
              QUESTIONNAIRE_TENX_DATA.medicalHistory?.options ?? []
            ).find((o) => toLower(o.name) === toLower(medical.name));

            if (found) {
              selected.push(found);
            } else {
              selectedOthers.push(medical);
            }
          }

          valueMedicalHistory = {
            ...valueMedicalHistory,
            medicalHistory: concat(
              selected.length > 1
                ? selected.filter(({ name }) => toLower(name) !== 'none')
                : selected,
              selectedOthers.length > 0
                ? [
                    {
                      name: selectedOthers.map((o) => o.name).join(', '),
                      value: 'OTHER'
                    }
                  ]
                : []
            )
          };
        }
        if (fatherMedicalHistory.length > 0) {
          const selected = [];
          const selectedOthers = [];

          for (const medical of fatherMedicalHistory) {
            const found = (
              QUESTIONNAIRE_TENX_DATA.medicalHistory?.options ?? []
            ).find((o) => toLower(o.name) === toLower(medical.name));

            if (found) {
              selected.push(found);
            } else {
              selectedOthers.push(medical);
            }
          }

          valueMedicalHistory = {
            ...valueMedicalHistory,
            fatherMedicalHistory: concat(
              selected.length > 1
                ? selected.filter(({ name }) => toLower(name) !== 'none')
                : selected,
              selectedOthers.length > 0
                ? [
                    {
                      name: selectedOthers.map((o) => o.name).join(', '),
                      value: 'OTHER'
                    }
                  ]
                : []
            )
          };
        }
        if (motherMedicalHistory.length > 0) {
          const selected = [];
          const selectedOthers = [];

          for (const medical of motherMedicalHistory) {
            const found = (
              QUESTIONNAIRE_TENX_DATA.medicalHistory?.options ?? []
            ).find((o) => toLower(o.name) === toLower(medical.name));

            if (found) {
              selected.push(found);
            } else {
              selectedOthers.push(medical);
            }
          }
          valueMedicalHistory = {
            ...valueMedicalHistory,
            motherMedicalHistory: concat(
              selected.length > 1
                ? selected.filter(({ name }) => toLower(name) !== 'none')
                : selected,
              selectedOthers.length > 0
                ? [
                    {
                      name: selectedOthers.map((o) => o.name).join(', '),
                      value: 'OTHER'
                    }
                  ]
                : []
            )
          };
        }
        form = {
          ...form,
          ...valueMedicalHistory
        };
      }

      return {
        ...state,
        form,
        userProfile: profile
      };
    }
  ),
  on(QuestionnaireTenxActions.setLoading, (state, { key, value }) => ({
    ...state,
    loading: {
      ...state.loading,
      [key]: value
    }
  })),
  on(
    QuestionnaireTenxActions.setSubmissionResponse,
    (state, { response, success, message }) => ({
      ...state,
      submissionResponse: {
        response,
        success,
        message
      }
    })
  )
);
